import { omit } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BuildingId, Contract, ContractId, OrgId, UtcTime } from 'shared/models';
import { toError } from 'shared/util/toError';
import { Permission } from 'shared/permissions';
import { useAsync, useSession } from 'hooks';
import { callApi } from 'apiClient';
import { getContracts } from 'selectors/contractsSelectors';
import { cacheContracts, cacheContractLockState } from 'actions/contractActions';

export type Options = {
    orgId?: OrgId | null;
    buildingId?: BuildingId | null;
};

type State = {
    contractIds: ContractId[];
    isLoaded: boolean;
    isBusy: boolean;
    errorKey?: string;
};

export function useContracts({ orgId, buildingId }: Options = {}) {
    const { hasPermission } = useSession();
    const { isMounted } = useAsync();

    const contracts = useSelector(getContracts);

    const [ state, setState ] = useState<State>({
        contractIds: [],
        isLoaded: false,
        isBusy: false,
    });

    const dispatch = useDispatch();

    const loadContracts = useCallback((apiEndpoint: string) => {
        if (!hasPermission(null, Permission.Admin)) {
            setState({
                contractIds: [],
                isLoaded: true,
                isBusy: false,
            });
            return;
        }
        
        setState({
            contractIds: [],
            isLoaded: false,
            isBusy: true,
        });
        void (async () => {
            try {
                const contracts = await callApi<Record<ContractId, Contract>>('GET', apiEndpoint);
                dispatch(cacheContracts(contracts));
                isMounted() && setState({
                    contractIds: Object.keys(contracts),
                    isLoaded: true,
                    isBusy: false,
                });
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    isBusy: false,
                    errorKey: err.message,
                }));
            }
        })();
    }, [ isMounted, setState, dispatch ]);

    const loadContractsByBuilding = useCallback((buildingId: BuildingId) => {
        loadContracts(`/api/buildings/${buildingId}/contracts`);
    }, [ loadContracts ]);

    const loadContractsByOrg = useCallback((orgId: OrgId) => {
        loadContracts(`/api/orgs/${orgId}/contracts`);
    }, [ loadContracts ]);

    const lockContract = useCallback((contractId: ContractId, lock: boolean) => {
        setState(state => ({ ...state, isBusy: true }));
        void (async () => {
            try {
                const { locked } = await callApi<Contract>('POST', `/api/contracts/${contractId}/${lock ? 'lock' : 'unlock'}`);
                dispatch(cacheContractLockState(contractId, { locked }));
                isMounted() && setState(({ contractIds }) => ({
                    contractIds,
                    isBusy: false,
                    isLoaded: true,
                }));
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    isBusy: false,
                    errorKey: err.message,
                }));
            }
        })();
    }, [ setState, isMounted ]);

    const renewContract = useCallback((contractId: ContractId, paymentDate: UtcTime, expiryDate: UtcTime, replaceContractIds: ContractId[]): Promise<boolean> => {
        setState(state => ({ ...state, isBusy: true }));
        return (async () => {
            try {
                const orgContracts = await callApi<Record<ContractId, Contract>>('POST', `/api/contracts/${contractId}/renew`, {
                    paymentDate,
                    expiryDate,
                    replaceContractIds,
                });
                dispatch(cacheContracts(orgContracts));
                setState(state => ({
                    ...omit(state, 'errorKey'),
                    isBusy: false,
                }));
                return true;
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    isBusy: false,
                    errorKey: err.message,
                }));
                return false;
            }
        })();
    }, [ isMounted, setState ]);

    const deleteContract = useCallback((contractId: ContractId) => {
        setState(state => ({ ...state, isBusy: true }));
        void (async () => {
            try {
                await callApi('DELETE', `/api/contracts/${contractId}`);
                isMounted() && setState(({ contractIds }) => ({
                    contractIds: contractIds.filter(id => id !== contractId),
                    isBusy: false,
                    isLoaded: true,
                }));
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    isBusy: false,
                    errorKey: err.message,
                }));
            }
        })();
    }, [ setState, isMounted ]);
    
    const createContract = useCallback((data: Omit<Contract, 'id' | 'locked' | 'created' | 'updated'>, replaceContractIds: ContractId[]) => {
        setState(state => ({ ...state, isBusy: true }));
        return (async () => {
            try {
                const { orgId, ...body } = data;
                const orgContracts = await callApi<Record<ContractId, Contract>>('POST', `/api/orgs/${orgId}/contracts`, {
                    ...body,
                    replaceContractIds,
                });
                dispatch(cacheContracts(orgContracts));
                isMounted() && setState(state => ({
                    ...omit(state, 'errorKey'),
                    contractIds: Object.keys(orgContracts), // This assumes we only create in the context of a list of Contracts by Org
                    isBusy: false,
                    isLoaded: true,
                }));
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    isBusy: false,
                    errorKey: err.message,
                }));
            }
        })();
    }, [ setState, isMounted, dispatch ]);

    useEffect(() => {
        if (orgId) {
            loadContractsByOrg(orgId);
        }
        else if (buildingId) {
            loadContractsByBuilding(buildingId);
        }
        else {
            setState({
                contractIds: [],
                isBusy: false,
                isLoaded: true,
            });
        }
    }, [ orgId, buildingId, setState, loadContractsByBuilding, loadContractsByOrg ]);

    return {
        ...state,
        contracts,
        createContract,
        lockContract,
        renewContract,
        deleteContract,
    };
}
